import { Link } from 'react-router-dom';
import GenreChip from './GenreChip';
import styles from './EventCard.module.css';

interface EventCardProps {
  id: string;
  slug: string | null;
  artist: { name: string; genre: string | null };
  venue: { name: string; distanceMiles?: number };
  eventDate: string;
  startTime: string | null;
  ticketPrice: string | null;
  coverCharge: boolean | null;
  status: string;
}

function formatDate(iso: string) {
  const d = new Date(`${iso}T12:00:00`);
  return {
    day: d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
    month: d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
    date: d.getDate(),
  };
}

function formatTime(t: string | null) {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

function priceLabel(price: string | null, cover: boolean | null) {
  if (price) return price;
  if (cover === false) return 'Free';
  if (cover === true) return 'Cover charge';
  return null;
}

export default function EventCard({ id, slug, artist, venue, eventDate, startTime, ticketPrice, coverCharge, status }: EventCardProps) {
  const { day, month, date } = formatDate(eventDate);
  const isCancelled = status === 'cancelled';

  return (
    <Link
      to={`/events/${slug ?? id}`}
      className={[styles.card, isCancelled ? styles.cancelled : ''].join(' ')}
      aria-label={`${artist.name} at ${venue.name} on ${month} ${date}`}
    >
      <div className={styles.dateBadge}>
        <span className={styles.dateDay}>{day}</span>
        <span className={styles.dateNum}>{date}</span>
        <span className={styles.dateMon}>{month}</span>
      </div>
      <div className={styles.body}>
        <div className={styles.venueRow}>
          <span className={styles.venueName}>{venue.name}</span>
          {venue.distanceMiles !== undefined && (
            <span className={styles.distance}>{venue.distanceMiles} mi</span>
          )}
        </div>
        <div className={styles.artistName}>{artist.name}</div>
        <div className={styles.meta}>
          <GenreChip genre={artist.genre} />
          {startTime && <span className={styles.time}>{formatTime(startTime)}</span>}
        </div>
        {priceLabel(ticketPrice, coverCharge) && (
          <div className={styles.price}>{priceLabel(ticketPrice, coverCharge)}</div>
        )}
        {isCancelled && <div className={styles.cancelledBadge}>CANCELLED</div>}
      </div>
    </Link>
  );
}
